'use client'

import type { ReactNode } from 'react'
import { ChevronRight } from 'react-feather'
import { Link, useTranslations } from '@/lib/next-shim'

type DashboardSectionProps = {
  title: string
  /** Item count shown as a badge next to the title; also gates the "view all" action. */
  count?: number
  /** Route the "view all" action to a page (renders a Link). */
  viewAllHref?: string
  /** Or handle "view all" imperatively (renders a button). Ignored when viewAllHref is set. */
  onViewAll?: () => void
  children: ReactNode
}

const viewAllClassName =
  'inline-flex shrink-0 items-center gap-0.5 rounded-md text-sm font-medium text-primary transition-colors hover:text-primary/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'

export function DashboardSection({ title, count = 0, viewAllHref, onViewAll, children }: DashboardSectionProps) {
  const t = useTranslations()
  const hasItems = count > 0
  const showViewAll = hasItems && (viewAllHref || onViewAll)

  return (
    <section className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between gap-3 px-5">
        <div className="flex items-center gap-2.5">
          <h3 className="section-title">{title}</h3>
          {hasItems && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-primary">{count}</span>}
        </div>
        {showViewAll &&
          (viewAllHref ? (
            <Link href={viewAllHref} className={viewAllClassName}>
              {t('common.viewAll')}
              <ChevronRight size={16} aria-hidden="true" />
            </Link>
          ) : (
            <button type="button" onClick={onViewAll} className={viewAllClassName}>
              {t('common.viewAll')}
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          ))}
      </div>
      {children}
    </section>
  )
}
